import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "cbd_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      console.error("Failed to load cart from localStorage:", error);
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error("Failed to persist cart to localStorage:", error);
    }
  }, [items]);

  const add = useCallback((book, qty = 1) => {
    setItems((cur) => {
      const idx = cur.findIndex((i) => i.book_id === book.id);
      if (idx >= 0) {
        const next = [...cur];
        next[idx] = { ...next[idx], quantity: Math.min(50, next[idx].quantity + qty) };
        return next;
      }
      return [
        ...cur,
        {
          book_id: book.id,
          title: book.title,
          author: book.author,
          price: Number(book.price) || 0,
          has_cover: !!book.cover_path,
          quantity: qty,
        },
      ];
    });
  }, []);

  const setQty = useCallback((bookId, qty) => {
    setItems((cur) => cur.map((i) => (i.book_id === bookId ? { ...i, quantity: Math.max(1, Math.min(50, qty)) } : i)));
  }, []);

  const remove = useCallback((bookId) => setItems((cur) => cur.filter((i) => i.book_id !== bookId)), []);
  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);

  const value = useMemo(
    () => ({ items, count, subtotal, add, setQty, remove, clear, open, setOpen }),
    [items, count, subtotal, add, setQty, remove, clear, open]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
